import html2pdf from 'html2pdf.js';
import i18n, { getI18nLocale } from '@/i18n';
import de from '@/locales/de.json';
import en from '@/locales/en.json';
import { BlobURL } from '@/types/interfaces/blobUrl';

const locale = getI18nLocale() === 'en' ? en : de;

// Generate PDF
export function generatePDF(content: HTMLElement, filename: string): void {
  const options = {
    margin: [14, 14, 14, 14], // Set margin to 14 to fit all content
    filename: filename + '.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true, // Use CORS if necessary
      scrollX: 0, // Make sure there is no scroll
      scrollY: 0,
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    },
  };

  // HTML to PDF conversion process
  html2pdf().from(content).set(options).save();
}

export function getGeneratedPDFBlob(content: HTMLElement): Promise<string> {
  return new Promise((resolve, reject) => {
    const options = {
      margin: [2, 2, 2, 2],
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
    };

    html2pdf()
      .from(content)
      .set(options)
      .outputPdf('blob')
      .then((pdfBlob: string | PromiseLike<string>) => {
        resolve(pdfBlob);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function createPdfOfferTemplate(
  imageLogo: string,
  title: string,
  ordererInfo: string,
  imageProduct: string,
  productName: string,
  productPrice: string,
  productDescription: string,
  isCover: boolean
): HTMLElement {
  // Creating the main div element
  const container = document.createElement('div');
  container.style.boxSizing = 'border-box';
  container.style.backgroundColor = '#fff';
  container.style.minHeight = '268mm';
  container.style.maxHeight = '297mm';
  container.style.overflow = 'hidden';

  // Adding logo
  const logo = document.createElement('img');
  logo.src = imageLogo;
  logo.style.height = '40px';
  logo.style.display = 'block';
  logo.style.marginLeft = 'auto';
  logo.style.marginRight = 'auto';
  logo.style.marginBottom = '40px';
  container.appendChild(logo);

  // Adding title
  const mainHeading = document.createElement('h1');
  mainHeading.textContent = title;
  mainHeading.style.marginBottom = '40px';
  mainHeading.style.textAlign = 'center';
  container.appendChild(mainHeading);

  // Create table
  const table = document.createElement('table');
  table.style.borderCollapse = 'collapse';
  table.style.textAlign = 'left';
  table.style.width = '100%';
  // table.style.border = '1px solid';
  // table.style.borderColor = '#dddddd';

  // Info title column
  const infoTitle = document.createElement('th');
  infoTitle.textContent = i18n.global.t('components.order-summary.ordererInfo');
  infoTitle.style.padding = '5px';
  infoTitle.style.paddingTop = '0px';
  container.appendChild(infoTitle);

  // Adding Orderer Info
  const ordererCell = document.createElement('div');
  ordererCell.style.padding = '5px';
  ordererCell.style.paddingBottom = '40px';
  ordererCell.style.fontWeight = 'normal';
  ordererCell.innerHTML = ordererInfo;
  container.appendChild(ordererCell);

  // Adding the first row
  const row1 = document.createElement('tr');

  if (!isCover) {
    // Product image column
    const productImageCell = document.createElement('td');
    productImageCell.rowSpan = 2;
    productImageCell.style.width = '100px';
    productImageCell.style.paddingTop = '10px';

    const productImage = document.createElement('img');
    productImage.src = imageProduct;
    productImage.width = 100;
    productImageCell.appendChild(productImage);
    row1.appendChild(productImageCell);
  }

  // Product title column
  const productTitle = document.createElement('th');
  productTitle.textContent = productName; // Product Name
  productTitle.style.padding = '10px';
  productTitle.style.paddingTop = '0px !important';
  row1.appendChild(productTitle);

  // Product quantity column
  const quantityCell = document.createElement('td');
  quantityCell.textContent = '1 Stk';
  quantityCell.style.padding = '10px';
  productTitle.style.paddingTop = '0px !important';
  quantityCell.style.fontWeight = 'normal';
  row1.appendChild(quantityCell);

  // Product price column
  const priceCell = document.createElement('th');
  priceCell.textContent = `€ ${productPrice}`;
  priceCell.style.padding = '10px';
  productTitle.style.paddingTop = '0px !important';
  row1.appendChild(priceCell);

  table.appendChild(row1);

  // Adding a second row
  const row2 = document.createElement('tr');

  const descriptionCell = document.createElement('td');
  descriptionCell.colSpan = 3;
  descriptionCell.style.padding = '10px';
  descriptionCell.style.fontWeight = 'normal';
  descriptionCell.innerHTML = productDescription;
  row2.appendChild(descriptionCell);

  table.appendChild(row2);

  // Adding a table to a container
  container.appendChild(table);

  const bottomLine = document.createElement('div');
  bottomLine.style.width = '100%';
  bottomLine.style.height = '2px';
  bottomLine.style.backgroundColor = '#dddddd';
  bottomLine.style.marginTop = '20px';
  bottomLine.style.marginBottom = '40px';
  container.appendChild(bottomLine);

  // Adding the total price
  const totalPriceHeading = document.createElement('h3');
  totalPriceHeading.textContent = `Preis (zzgl. Mwst + zzgl. Versand): € ${productPrice}`;
  totalPriceHeading.style.textAlign = 'center';
  container.appendChild(totalPriceHeading);

  // Add payment information
  const paymentInfo = document.createElement('div');
  paymentInfo.textContent = 'Zahlungsbedingungen: Innerhalb von 7 Tage ohne Abzug';
  paymentInfo.style.textAlign = 'center';
  paymentInfo.style.fontWeight = 'normal';
  container.appendChild(paymentInfo);

  // Adding a footer
  const footerAddress = document.createElement('div');
  footerAddress.textContent = 'Granitweg 1 | 4202 | Kirchschlag bei Linz | Austria';
  footerAddress.style.fontWeight = 'normal';

  const footerBankInfo = document.createElement('div');
  footerBankInfo.textContent = ' Gramastetten-Herzogsdorf | IBAN: AT45 3413 5000 0716 3397 | BIC: RZOOAT2L13';
  footerBankInfo.style.fontWeight = 'normal';

  const footerContent = document.createElement('div');
  footerContent.style.paddingTop = '4mm';
  footerContent.style.paddingBottom = '4mm';
  footerContent.style.borderTop = '1px solid #dddddd';

  const footer = document.createElement('div');
  footer.style.position = 'absolute';
  footer.style.bottom = '-15px';
  footer.style.left = '0';
  footer.style.width = '100%';
  footer.style.textAlign = 'center';

  footerContent.appendChild(footerAddress);
  footerContent.appendChild(footerBankInfo);
  footer.appendChild(footerContent);
  container.appendChild(footer);


  return container;
}

 const aidddoLogoUrl = 'https://aidddoblob.blob.core.windows.net/public/Logo_Aidddo.jpg';

// Create Part List PDF Template
export function createPdfPartListTemplate(imageLogo: string, title: string, partList: any[]): HTMLElement {
  // Creating the main div element
  const container = document.createElement('div');
  container.style.boxSizing = 'border-box';
  container.style.backgroundColor = '#fff';

  // Adding a logo
  const logo = document.createElement('img');
  logo.src = imageLogo;
  logo.style.height = '40px';
  logo.style.display = 'block';
  logo.style.marginLeft = 'auto';
  logo.style.marginRight = 'auto';
  logo.style.marginBottom = '40px';
  container.appendChild(logo);

  // Add title
  const mainHeading = document.createElement('h1');
  mainHeading.textContent = title;
  mainHeading.style.marginBottom = '50px';
  mainHeading.style.textAlign = 'center';
  container.appendChild(mainHeading);

  for (let index = 0; index < partList.length; index++) {
    const table = document.createElement('table');
    table.style.borderCollapse = 'collapse';
    table.style.textAlign = 'left';
    table.style.width = '100%';

    // Adding the first row
    const row1 = document.createElement('tr');

    // Product image column
    const productImageCell = document.createElement('td');
    productImageCell.rowSpan = partList[index].parts.length * 4;
    productImageCell.width = '100px';
    productImageCell.style.paddingRight = '16px';

    const productImage = document.createElement('img');
    productImage.src = partList[index].parts[0].thumbnailUrl;
    productImage.style.width = '100px';
    productImageCell.appendChild(productImage);
    row1.appendChild(productImageCell);

    const productTitleSize = document.createElement('th');
    productTitleSize.textContent = partList[index].label;
    productTitleSize.style.paddingBottom = '4px';
    productTitleSize.style.textTransform = 'capitalize';
    row1.appendChild(productTitleSize);

    const productTitleQty = document.createElement('th');
    productTitleQty.textContent = 'Qty';
    productTitleQty.style.paddingBottom = '4px';
    productImageCell.width = '50px';
    row1.appendChild(productTitleQty);

    const productTitlePrice = document.createElement('th');
    productTitlePrice.textContent = 'Price';
    productTitlePrice.style.paddingBottom = '4px';
    productImageCell.width = '100px';
    row1.appendChild(productTitlePrice);
    table.appendChild(row1);

    for (let key = 0; key < partList[index].parts.length; key++) {
      const row2 = document.createElement('tr');

      const prudctSizeWidth = document.createElement('td');
      prudctSizeWidth.textContent = partList[index].parts[key].width;
      prudctSizeWidth.style.padding = '0';
      prudctSizeWidth.style.fontWeight = 'normal';
      row2.appendChild(prudctSizeWidth);

      const prudctQty = document.createElement('td');
      prudctQty.textContent = partList[index].parts[key].cnt + 'x';
      prudctQty.style.fontWeight = 'normal';
      prudctQty.style.padding = '0';
      prudctQty.width = '50px';
      row2.appendChild(prudctQty);

      const prudctPrice = document.createElement('th');
      prudctPrice.textContent = partList[index].parts[key].totalPrice.toFixed(2) + ' €';
      prudctPrice.style.fontWeight = 'normal';
      prudctPrice.style.padding = '0';
      prudctPrice.width = '100px';
      row2.appendChild(prudctPrice);
      table.appendChild(row2);

      const row3 = document.createElement('tr');

      const prudctSizeHeight = document.createElement('td');
      prudctSizeHeight.textContent = partList[index].parts[key].height;
      prudctSizeHeight.style.padding = '0';
      prudctSizeHeight.style.fontWeight = 'normal';
      row3.appendChild(prudctSizeHeight);
      table.appendChild(row3);

      const row4 = document.createElement('tr');
      const prudctSizeDepth = document.createElement('td');
      prudctSizeDepth.textContent = partList[index].parts[key].depth;

      if (partList[index].parts.length > 1 && key < partList[index].parts.length) {
        prudctSizeDepth.style.paddingBottom = '10px';
      } else {
        prudctSizeDepth.style.paddingBottom = '0';
      }

      prudctSizeDepth.style.fontWeight = 'normal';
      row4.appendChild(prudctSizeDepth);
      table.appendChild(row4);
    }

    container.appendChild(table);

    const bottomLine = document.createElement('div');
    bottomLine.style.width = '100%';
    bottomLine.style.height = '1px';
    bottomLine.style.backgroundColor = '#dddddd';
    bottomLine.style.marginTop = '20px';
    bottomLine.style.marginBottom = '20px';

    container.appendChild(bottomLine);
  }

  return container;
}

export function createPDFRenderObjectView(blobURLs: BlobURL[]): HTMLElement {
  console.log('blobURLs', blobURLs);
  // Creating the main container div element
  const container: HTMLElement = document.createElement('div');
  container.style.display = 'flex';
  container.style.flexDirection = 'column';

  // Iterate through each item in the blobURLs array
  blobURLs.forEach((data) => {
    // Create a div element for each item
    const itemContainer = document.createElement('div');
    itemContainer.style.boxSizing = 'border-box';
    itemContainer.style.backgroundColor = '#ffffff';
    itemContainer.style.marginBottom = '40px';

    // Create an h4 element for the name
    const name = document.createElement('h4');
    name.textContent = data.name; // Using template literals
    itemContainer.appendChild(name);

    // Adding a logo
    const imageContainer = document.createElement('div');
    imageContainer.style.backgroundColor = '#efefef';
    imageContainer.style.display = 'flex';
    imageContainer.style.justifyContent = 'center';

    const image = document.createElement('img');
    image.src = data.blobUrl;
    image.style.backgroundColor = '#efefef';
    image.style.height = '440px';
    imageContainer.appendChild(image);

    itemContainer.appendChild(imageContainer);

    // Add itemContainer to the main container
    container.appendChild(itemContainer);
  });

  // Returns the container element containing all items
  return container;
}

export function getOrderConfirmationPDFTemplate(data: Record<string, string>): string {
  const t    = i18n.global.t.bind(i18n.global);
  const lang = i18n.global.locale;

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${t('components.order-summary.title')}</title>
  <style>
    *{
      font-weight: 400;
    }
  </style>
</head>
<body style="font-family: Roboto, Arial, Helvetica, sans-serif; margin: 0; padding: 0; background-color: #f9f9f9;">

  <!-- Main container -->
  <div style="max-width: 700px; margin: 20px auto; background: #ffffff; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
    
    <!-- Header -->
    <div style="text-align: center; margin-bottom: 56px;">
      <img src="${data.logoUrl}" alt="Company Logo" style="max-width: 150px; margin-bottom: 24px;">
      <h1 style="font-size: 32px; margin: 0; font-weight: 500;">${t('pdfTemplates.orderConfirmation.orderConfirmTitle')}</h1>
    </div>
    
    <!-- Order Details -->
    <div style="text-align: center; margin-bottom: 30px;">
      <p style="font-size: 24px; margin: 0;">Order details</p>
    </div>
    
    <!-- Product Details -->
    <div style="background-color: ${data.bgColor}; padding: 20px; border-radius: 4px; margin-bottom: 20px; text-align: center; border-top: 4px solid #838383; border-bottom: 4px solid #838383;">
      <h3 style="font-size: 24px; font-weight: bold; color:${data.textColor};">${data.coverModel}</h3>
      <div style="display: flex; justify-content: center; margin-top: 42px;">
        <div style="text-align: left; margin-right: 32px; color:${data.textColor};">
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.bodySide')}: ${data.bodySide}</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.calfCircumference')}: ${data.depth} mm</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.ankleCircumference')}: ${data.calfCircumferenceWithoutDeduction} mm</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.color')}: ${data.color}</p>
        </div>
        <div style="text-align: left; color:${data.textColor};">
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.kneeJoint')}: ${data.kneeJoint}</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.heightWithoutDeduction')}: ${data.heightWithoutDeduction} mm</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.embossing')}: ${data.embossing}</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.zipColor')}: ${data.zipColor}</p>
        </div>
      </div>
       <!-- Price Details -->
      <div style="width: 100%; height: 1px; background-color: #d7d7d7; margin-top: 32px; margin-bottom: 32px;"></div>
      <div style="display: flex; gap: 52px; justify-content: center; margin-bottom: 20px;">
        <div style="text-align: center;">
          <div style="flex: 1; color: rgb(100, 100, 100); color:${data.textColor};">${t('pdfTemplates.orderConfirmation.quantity')}</div>
          <div style="flex: 1; font-size: 32px; color:${data.textColor};"><b>${data.quantity}</b></div>
        </div>
        <div style="text-align: center;">
          <div style="flex: 1; color: rgb(100, 100, 100); color:${data.textColor};">${t('pdfTemplates.orderConfirmation.priceTitle')}</div>
          <div style="flex: 1; font-size: 32px; color:${data.textColor};"><b>${data.price}</b></div>
        </div>
      </div>
    </div>
   
    <!-- Total -->
    <div style="display: flex; text-align: center; justify-content: center; align-items: center; gap: 16px; margin-bottom: 10px;">
      <p style="font-size: 32px; margin: 0; color: rgb(100, 100, 100);">${t('pdfTemplates.orderConfirmation.TotalPrice')}:</p>
      <p style="font-size: 32px; margin: 0;">${data.price}</p>
    </div>
    
    <!-- Footer -->
    <div style="text-align: center; font-size: 14px; color: #666; margin-top: 24px;">
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.VatAndShipping')}</p>
    </div>
    <div style="width: 100%; height: 1px; background-color: #d7d7d7; margin-top: 32px; margin-bottom: 32px;"></div>
    <div style="text-align: center; font-size: 12px; color: #666; margin-top: 10px;">
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.footerBy')}</p>
      <br><img src="${aidddoLogoUrl}" alt="Aidddo Logo" style="max-width: 90px;">
    </div>
  </div>

</body>
</html>`;
}

export function getOrderConfirmationPDFTemplateWjtOrtho(data: Record<string, string>): string {
  const t    = i18n.global.t.bind(i18n.global);
  const lang = i18n.global.locale;

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${t('components.order-summary.title')}</title>
  <style>
    *{
      font-weight: 400;
    }
  </style>
</head>
<body style="font-family: Roboto, Arial, Helvetica, sans-serif; margin: 0; padding: 0; background-color: #f9f9f9;">

  <!-- Main container -->
  <div style="max-width: 700px; margin: 20px auto; background: #ffffff; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
    
    <!-- Header -->
    <div style="text-align: center; margin-bottom: 56px;">
      <img src="${data.logoUrl}" alt="Company Logo" style="max-width: 150px; margin-bottom: 24px;">
      <br><br>
      <h1 style="font-size: 32px; margin: 0; font-weight: 500;">${t('pdfTemplates.orderConfirmation.orderConfirmTitle')}</h1>
    </div>

    <!-- Product Details -->
    <div style="background-color: ${data.bgColor}; padding: 20px; border-radius: 4px; margin-bottom: 20px; text-align: center; border-top: 4px solid #838383; border-bottom: 4px solid #838383;">
      <h3 style="font-size: 24px; font-weight: bold; color:${data.textColor};">REF: ${data.coverModel}</h3>
      <div style="display: flex; justify-content: center; margin-top: 42px;">
        <div style="text-align: left; margin-right: 32px; color:${data.textColor};">
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.bodySide')}: ${data.bodySide}</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.calfCircumference')}: ${data.depth} mm</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.ankleCircumference')}: ${data.calfCircumferenceWithoutDeduction} mm</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.color')}: ${data.color}</p>
        </div>
        <div style="text-align: left; color:${data.textColor};">
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.kneeJoint')}: ${data.kneeJoint}</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.heightWithoutDeduction')}: ${data.heightWithoutDeduction} mm</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.embossing')}: ${data.embossing}</p>
          <p style="margin: 5px 0;">${t('pdfTemplates.orderConfirmation.zipColor')}: ${data.zipColor}</p>
        </div>
      </div>
      
      <!-- Price Details -->
      <div style="width: 100%; height: 1px; background-color: #d7d7d7; margin-top: 32px; margin-bottom: 32px;"></div>
      <div style="display: flex; gap: 52px; justify-content: center; margin-bottom: 20px;">
        <div style="text-align: center;">
          <div style="flex: 1; color: rgb(100, 100, 100); color:${data.textColor};">${t('pdfTemplates.orderConfirmation.quantity')}</div>
          <div style="flex: 1; font-size: 32px; color:${data.textColor};"><b>${data.quantity}</b></div>
        </div>
      </div>
    </div>
    
    <!-- Footer -->
    <div style="text-align: center; font-size: 14px; color: #666; margin-top: 24px;">
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.thankYou')}</p> <br>
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.noTechQuestionsNotice')}</p><br>
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.contactInfo')}</p><br>
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.bestRegards')}</p>
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.teamSignature')}</p>
    </div>
    <div style="width: 100%; height: 1px; background-color: #d7d7d7; margin-top: 32px; margin-bottom: 32px;"></div>
    <div style="text-align: center; font-size: 12px; color: #666; margin-top: 10px;">
      <p style="margin: 0;">${t('pdfTemplates.orderConfirmation.footerBy')}</p>
      <br><img src="${aidddoLogoUrl}" alt="Aidddo Logo" style="max-width: 90px;">
    </div>
  </div>

</body>
</html>`;
}
